import React, { useState } from "react";
import { PhoneInput } from "../components/ui/phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useAuth } from "../components/context/auth/AuthState";
import { Link, useLocation, Navigate, useNavigate, useParams } from "react-router-dom";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, CheckCircle2, Leaf } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { currentUser, login } = useAuth();

  const [status, setStatus] = useState({ msg: "", type: "success" });
  const [phno, setPhno] = useState({ phone: "", valid: false });
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChangePhno = (value) => {
    setPhno({
      phone: value,
      valid: value ? isValidPhoneNumber(value) : false
    });
    setStatus({ msg: "", type: "success" });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ msg: "", type: "success" });

    if (!phno.valid) {
      setStatus({ msg: "Invalid Phone Number", type: "error" });
      setLoading(false);
      return;
    }

    try {
      await login(phno.phone, password);
      if (id) {
        navigate(`/join/${id}`);
      } else {
        navigate("/");
      }
    } catch (error) {
      setStatus({
        msg: error.message || "Login Failed",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (currentUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return (
    <div className="w-full h-screen lg:grid lg:grid-cols-2 overflow-hidden">
      {/* Left Side - Form */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-white overflow-y-auto h-full">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Leaf className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to your Healthier Me account
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            {status.msg && (
              <Alert
                variant={status.type === "error" ? "destructive" : "default"}
                className={`border ${status.type === "error" ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}
              >
                {status.type === "error" ? (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                )}
                <AlertDescription className={status.type === "error" ? "text-red-800" : "text-emerald-800"}>
                  {status.msg}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
                <PhoneInput
                  value={phno.phone}
                  onChange={handleChangePhno}
                  defaultCountry="IN"
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-emerald-600 hover:text-emerald-500"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 h-auto text-base font-medium shadow-sm transition-all duration-200"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>

            <div className="text-center text-sm">
              <span className="text-gray-500">Don't have an account? </span>
              <Link
                to={id ? `/signup/${id}` : "/signup"}
                className="font-medium text-emerald-600 hover:text-emerald-500"
              >
                Create an account
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block relative h-full bg-gray-50">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1540420773420-3366772f4999?q=80&w=1920&auto=format&fit=crop"
          alt="Healthy lifestyle"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-12">
          <div className="text-white space-y-4 max-w-lg">
            <h3 className="text-3xl font-bold">Your Journey to a Healthier You</h3>
            <p className="text-lg text-gray-100">
              Track your progress, join challenges, and build healthy habits with our community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
